import { Box, Container, IconButton, Typography } from "@material-ui/core"
import { LeadInfoPageModel, postLeadCreateLead } from "api"
import { useIsDesktop } from "common/MediaQueries"
import GenericButton from "components/GenericButton/GenericButton"
import { GenericInput } from "components/GenericInput/GenericInput"
import { useCompaniesContext } from "components/PurchasePage/CompaniesContext"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { formatPhoneNumber } from "components/Reusable/Functions/Functions"
import Image from "next/image"
import { useRouter } from "next/router"
import { Dispatch, SetStateAction, createRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import styled from "styled-components"
import BackIcon from "../../components/Icons/BackIcon"
import CompaniesForm from "./CompaniesForm"
import SocialSection from "./SocialSection"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledCenteredContent = styled.div`
  display: flex;
  justify-content: center;
`

const StyledWrapper = styled(Container)`
  margin-top: 79px;
  padding-right: 0px;
  padding-left: 0px;
  width: 100%;

  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    margin-top: 40px;
    width: 1160px;
  }
`

const StyledImageWrapper = styled(Box)`
  position: relative;
  top: -70px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    height: 250px;
    top: 0px;
  }
`

const StyledImage = styled(Image)`
  border-radius: 8px;
`

const SvgWrapper = styled(Box)`
  position: absolute;
  z-index: 9;
  padding-top: 16px;
  padding-right: 16px;
`

const StyledTitle = styled(Typography).attrs({ variant: "h1" })`
  text-align: right;
  margin-top: 20px;
  font-size: 40px;
  line-height: 46px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 0px;
  }
`

const StyledSubtitle = styled(Typography).attrs({ variant: "body1" })`
  text-align: right;
  margin-bottom: 37px;
  word-break: break-word;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 10px;
    margin-bottom: 60px;
  }
`

const StyledGenericInput = styled(GenericInput)`
  padding-bottom: 4px;
`

const StyledButtonWrapper = styled(Box)`
  width: 300px;
  margin-bottom: 40px;
  margin-top: 60px;
  position: relative;
  border-radius: 8px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 20px;
    position: fixed;
    bottom: 0;
    right: 0;
    width: calc(100% - 32px);
    border-radius: 10px;
    box-shadow: 0 -5px 10px 0 hsla(0, 0%, 0%, 0.12);
    background-color: #fff;
    padding: 12px 16px 10px 16px;
    margin-top: 0px;
    z-index: 999;
    margin-bottom: 0px;
  }
  box-sizing: content-box;
`

const StyledButton = styled(GenericButton)<{ isDisabled?: boolean }>`
  ${(p) =>
    p.isDisabled &&
    `
    background: var(--Light-Grey, #f7f7f7);
    color: rgba(45, 45, 45, 0.72);
    font-size: 24px;
    :hover {
    background: var(--Light-Grey, #f7f7f7);

  } `}
`

const StyledMain = styled(Box)`
  padding-left: 16px;
  padding-right: 16px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 0;
  }
`

const StyledFieldWrapper = styled(Box)`
  > * {
    width: 100%;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 30px 0;
    .MuiFormControl-root {
      margin-left: 10px;
    }

    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    > * {
      width: 220px;
    }
  }
`
const StyledLink = styled.div`
  display: flex;
  cursor: pointer;
  p {
    margin-right: 8px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 120px;
    padding: 0 40px;
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    svg {
      circle {
        fill: white;
      }
    }
  }
  :hover {
    text-decoration: underline;
  }
`

const StyledNotesWrapper = styled(Box)`
  width: 100%;
  .MuiFormControl-root {
    width: 100%;
    ${(p) => p.theme.breakpoints.down("sm")} {
      height: auto;
    }
  }
  textarea {
    font-size: 20px;
    height: 100% !important;
    font-family: Speedee, NarkisBlock, sans-serif;
    font-weight: 500;
  }
  .MuiFormHelperText-root {
    display: none;
  }
`

const StyledOveralError = styled(Typography).attrs({ variant: "body1" })<{
  $isDesktop?: boolean
}>`
  font-size: 18px;
  line-height: 24px;
  color: #d70202;
  text-align: right;
  ${(p) => p.$isDesktop && "position: absolute"};
  ${(p) => p.$isDesktop && "bottom: 50px"};
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-top: 10px;
  }
`
const StyledIconBtn = styled(IconButton)`
  padding: 0px;
`

const StyledTextBox = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 680px;
  }
`

const StyledTypography = styled(Typography).attrs({ variant: "body1" })`
  margin-top: 7px;
`

const StyledBackWrapper = styled(Container)``

type CompaniesPageProps = {
  data: LeadInfoPageModel
  setCompaniesSteps?: Dispatch<SetStateAction<number>>
}

const CompaniesPage = ({ data, setCompaniesSteps }: CompaniesPageProps) => {
  const isDesktop = useIsDesktop()
  const reCaptchaRef = createRef<ReCAPTCHA>()
  const [isValid, setIsValid] = useState(true)
  const { setCompaniesData, companiesData } = useCompaniesContext()
  const companiesInitialValues = {
    firstName: companiesData?.firstName || "",
    lastName: companiesData?.lastName || "",
    companyName: companiesData?.companyName || "",
    companyIdNumber: companiesData?.companyIdNumber || "",
    phoneNumber: companiesData?.phoneNumber || "",
    email: companiesData?.email?.trim() || "",
    notes: companiesData?.notes || "",
    gRecaptchaResponse: true,
  }
  const router = useRouter()

  const handlePhoneInput = (evt, formik, fieldName) => {
    if (evt.target.value.length) {
      const formattedValue = formatPhoneNumber(evt.target.value)
      setTimeout(() => {
        formik.setFieldValue(fieldName, formattedValue)
      }, 0)
    }
  }

  const formIsValid = (formik) => {
    setIsValid(formik.isValid && Object.keys(formik.touched).length > 0)
  }

  const saveCompaniesData = (values) => setCompaniesData(values)

  return (
    <>
      {isDesktop && (
        <StyledBackWrapper>
          <StyledLink
            onClick={() => router.back()}
            onKeyDown={(e) => {
              if (e.key === "Enter") router.back()
            }}
          >
            <StyledIconBtn aria-label="חזרה לעמוד קודם">
              <BackIcon />
            </StyledIconBtn>
            <Typography variant="body1">חזרה</Typography>
          </StyledLink>
        </StyledBackWrapper>
      )}
      <StyledCenteredContent>
        <StyledWrapper>
          <StyledImageWrapper>
            {!isDesktop && (
              <SvgWrapper>
                <StyledIconBtn onClick={() => router.back()}>
                  <BackIcon />
                </StyledIconBtn>
              </SvgWrapper>
            )}
            <StyledImage
              src={"/girl-with-chips.jpg"}
              layout={!isDesktop ? "fill" : "fixed"}
              width={isDesktop && "404x"}
              height={isDesktop && "267px"}
              alt="גיפטקארד מקדונלד'ס לעובדים"
            />
            {isDesktop && (
              <SocialSection
                phone={data.customerServiceNumber}
                whatsapp={data.whatsappLink}
              />
            )}
          </StyledImageWrapper>
          <StyledMain>
            <StyledTitle>גיפטקארד מקדונלד&apos;ס לעובדים</StyledTitle>
            <StyledSubtitle>
              {data?.body
                ? data.body
                : "מזינים את הפרטים ונחזור אליכם בקרוב עם הצעה שווה ומשתלמת לעובדים שלכם!"}
            </StyledSubtitle>
            <FormikForm
              validationSchema={CompaniesForm.schemaCompanies}
              initialValues={companiesInitialValues}
              onSubmit={async (values) => {
                reCaptchaRef?.current?.reset()
                const recaptchaResponse =
                  await reCaptchaRef?.current?.executeAsync()
                try {
                  saveCompaniesData(values)
                  await postLeadCreateLead({
                    ...values,
                    gRecaptchaResponse: recaptchaResponse,
                  })
                  setCompaniesSteps(3)
                  setCompaniesData(null)
                } catch (error) {
                  setCompaniesSteps(2)
                }
              }}
              render={(formik) => (
                <>
                  <StyledFieldWrapper>
                    <StyledGenericInput
                      $width="220"
                      name="firstName"
                      label="שם פרטי (*חובה)"
                      type="text"
                      maxLength={30}
                      autoComplete="given-name"
                      topMargin="0"
                    />
                    <StyledGenericInput
                      $width="220"
                      name="lastName"
                      label="שם משפחה (*חובה)"
                      type="text"
                      maxLength={30}
                      autoComplete="given-name"
                      topMargin="0"
                    />
                    <StyledGenericInput
                      $width="220"
                      name="phoneNumber"
                      label="טלפון (*חובה)"
                      type="text"
                      numeric
                      autoComplete="tel"
                      topMargin="0"
                      onInput={(e) =>
                        handlePhoneInput(e, formik, "phoneNumber")
                      }
                      onPaste={async (e) => {
                        const clipboardData = e.clipboardData.getData("Text")
                        e.clipboardData.clearData()
                        setTimeout(async () => {
                          const phone = formatPhoneNumber(clipboardData)
                          formik.setFieldValue("phoneNumber", phone)
                        }, 300)
                      }}
                    />

                    <StyledGenericInput
                      $width="220"
                      name="email"
                      label="אימייל (*חובה)"
                      type="text"
                      autoComplete="email"
                      inputMode="email"
                      $unicodeBidi={
                        formik.values.email.match(/[a-zA-Z]/)
                          ? "plaintext"
                          : "normal"
                      }
                      topMargin="0"
                    />
                    <StyledGenericInput
                      $width="220"
                      name="companyName"
                      label="שם החברה (*חובה)"
                      type="text"
                      maxLength={30}
                      autoComplete="given-name"
                      topMargin="0"
                    />
                    <StyledGenericInput
                      $width="220"
                      name="companyIdNumber"
                      label="מספר ח.פ. (*חובה)"
                      type="text"
                      maxLength={9}
                      autoComplete="given-name"
                      topMargin="0"
                    />
                    <StyledNotesWrapper>
                      <StyledGenericInput
                        name="notes"
                        $width="680"
                        $height="120"
                        label="הערות"
                        type="text"
                        InputProps={{ multiline: true }}
                        maxLength={500}
                        topMargin={"0"}
                      />
                      <StyledTextBox>
                        <StyledTypography>
                          <Box
                            component="span"
                            fontWeight={
                              formik.values?.notes?.length > 0
                                ? "bold"
                                : "normal"
                            }
                          >
                            {formik.values?.notes?.length || 0}
                          </Box>
                          /500
                        </StyledTypography>
                      </StyledTextBox>
                    </StyledNotesWrapper>
                  </StyledFieldWrapper>

                  <StyledButtonWrapper>
                    {!isValid && isDesktop && !formik.isValid && (
                      <StyledOveralError $isDesktop={isDesktop}>
                        השלימו את הפרטים הנכונים
                      </StyledOveralError>
                    )}
                    <StyledButton
                      type="submit"
                      btnType="filled"
                      isDisabled={
                        formik?.isSubmitting ||
                        !formik.isValid ||
                        Object.keys(formik.touched).length === 0
                      }
                      onClick={() => formIsValid(formik)}
                    >
                      שליחה
                    </StyledButton>
                  </StyledButtonWrapper>
                  <ReCAPTCHA
                    ref={reCaptchaRef}
                    sitekey={RECAPTCHA_KEY}
                    size="invisible"
                    hl="iw"
                    tabIndex={-1}
                  />
                  {!isDesktop && (
                    <SocialSection
                      phone={data.customerServiceNumber}
                      whatsapp={data.whatsappLink}
                    />
                  )}
                  {!isValid && !isDesktop && !formik.isValid && (
                    <StyledOveralError>
                      השלימו את הפרטים הנכונים
                    </StyledOveralError>
                  )}
                </>
              )}
            ></FormikForm>
          </StyledMain>
        </StyledWrapper>
      </StyledCenteredContent>
    </>
  )
}

export default CompaniesPage
